import React from "react";

import { Link } from "react-router-dom";
import * as yup from "yup";

import { Tag, Typography } from "clearsense-ui/components";
import ActionDropdown from "components/Common/ActionDropdown";
import { DoneCheck } from "components/Common/clearsense-ui/icons";

const getStatusColor = text => {
  switch (text) {
    case "success":
      return "#EBF4EC";
    case "Started":
      return "#E8F4FD";
    case "Failed":
      return "#FAEAE";
    default:
      return "";
  }
};

export const databaseConnection = [
  "Choose an existing database",
  "Connect to a new database",
];

export const META_IQ_BASE_URL = "/management/reference-data/meta-iq";

export const CATEGORIES = [
  "Allergy category",
  "Allergy description",
  "Coding scheme",
  "Dose units",
  "Frequency units",
  "Gender",
  "ICD10",
  "Language",
  "Location",
  "Medical instructions",
  "Medication",
  "Person",
  "Problem/Disease",
  "Route",
  "Reaction",
  "Severity",
  "Status",
  "Zipcode",
].map(category => ({
  label: category,
  value: category,
  selected: false,
  show: true,
}));

export const DATABASE_TYPE = ["Postgres", "SQL Server", "MYSQL", "Oracle"].map(
  type => ({ value: type.toLowerCase(), label: type })
);

export const COLUMN = [
  {
    title: "Categories",
    width: 50,
    className: "justify-center flex",
    render: text => (
      <Typography style="medium" className="flex items-end w-full">
        {text}
      </Typography>
    ),
  },
];

export const DATABASE_DETAILS = [
  { value: "db_host", label: "Host Name" },
  { value: "db_port", label: "Port Number" },
  { value: "db_name", label: "Database Name" },
  { value: "db_user", label: "Database Username" },
  { value: "db_pass", label: "Database Password" },
  { value: "db_schema", label: "Database Schema" },
];

export const INITIAL_DATABASE_DETAILS = {
  db_host: "",
  db_port: "",
  db_name: "",
  db_user: "",
  db_pass: "",
  db_schema: "",
  db_type: "",
};

export const DATABASE_DETAILS_VALIDATION = yup.object().shape({
  db_host: yup.string().trim().required("Host name is required."),
  db_port: yup.string().trim().required("Port is required."),
  db_name: yup.string().trim().required("Database name is required."),
  db_user: yup.string().trim().required("Database username is required."),
  db_pass: yup.string().trim().required("Database password is required."),
  db_schema: yup.string().trim().required("Database schema is required."),
  db_type: yup.string().trim().required("Database type is required."),
});

export const REQUEST_TABLE_COLUMN = handleDelete => [
  {
    key: "classification_name",
    title: "Classification Name",
    width: 200,
    fixed: "left",
    render: (_, record) => {
      if (record.status === "success") {
        return (
          <Link
            className="cursor-pointer text-indigo-500"
            to={`/management/reference-data/meta-iq/results?audit_id=${record.audit_id}&name=${record.classification_name}`}
          >
            {record.classification_name}
          </Link>
        );
      }

      return (
        <Typography style="medium">{record.classification_name}</Typography>
      );
    },
  },
  {
    key: "db_name",
    dataIndex: "db_name",
    title: "Database Name",
    width: 150,
    render: text => <Typography style="medium"> {text}</Typography>,
  },
  {
    key: "categories_selected",
    dataIndex: "categories",
    title: "Categories Selected",
    width: 300,
    render: text => <Typography style="medium"> {text.join(", ")}</Typography>,
  },
  {
    key: "status",
    dataIndex: "status",
    title: "Status",
    width: 150,
    render: text => (
      <Tag
        label={text === "Started" ? "In-Progress" : text}
        color={getStatusColor(text)}
        className="flex-row-reverse "
      />
    ),
  },
  {
    key: "tablesScannes",
    dataIndex: "tablesScanned",
    title: "Tables Scanned",
    width: 150,
    render: text => <Typography style="medium">{text}</Typography>,
  },
  {
    key: "comments",
    dataIndex: "comments",
    title: "Comments",
    width: 300,
    render: text => <Typography style="medium">{text}</Typography>,
  },
  {
    title: "Action",
    dataIndex: "id",
    key: "action",
    align: "center",
    width: 80,
    render: (_, record) => (
      <ActionDropdown
        deleteProps={{
          onClick: () => handleDelete(record),
        }}
      />
    ),
  },
];

export const CATEGORIES_COLUMN = handleSelect => [
  {
    title: "Categories",
    key: "category",
    dataIndex: "value",
    sorter: (a, b) => a.value.localeCompare(b.value),
    render: (_, record) =>
      record.selected ? (
        <Typography
          className="flex justify-between"
          onClick={() => handleSelect(record)}
        >
          <Typography className="text-indigo-500 font-semibold">
            {record.value}
          </Typography>
          <DoneCheck className="text-indigo-500" />
        </Typography>
      ) : (
        <Typography onClick={() => handleSelect(record)}>
          {record.value}
        </Typography>
      ),
  },
];

export const RESULT_TABLE_COLUMN = [
  {
    key: "0",
    dataIndex: "Column",
    title: "Column",
    width: 200,
    render: text => (
      <Typography style="medium" className="h-8 flex items-center">
        {text.split("_").join(" ")}
      </Typography>
    ),
  },
  {
    key: "1",
    dataIndex: "Table",
    title: "Table",
    width: 300,
    render: text => <Typography style="medium"> {text}</Typography>,
  },
  {
    key: "2",
    dataIndex: "Category",
    title: "Category",
    width: 200,
    render: text => (
      <Typography style="medium"> {text.split("_").join(" ")}</Typography>
    ),
  },
];
